import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import UPZRegistrationsManager from '../components/admin/UPZRegistrationsManager';
import { LogOut, Building2, MapPin, Users, Plus, Trash2, CheckCircle2, ChevronRight, ShieldCheck, Palette, BarChart3, Save, Percent, CreditCard, Wheat, UserCheck, ClipboardList } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import RegionCascade from '../components/RegionCascade';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };
const inputCls = 'w-full px-3 py-2.5 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body text-sm';

/* ---------------- Masjid Tab ---------------- */
const MasjidTab = () => {
  const [mosques, setMosques] = useState([]);
  const [filter, setFilter] = useState('all');
  const [form, setForm] = useState({ name: '', village_id: '', address: '', latitude: '', longitude: '' });
  const [showForm, setShowForm] = useState(false);

  const load = () => {
    axios.get(`${API_URL}/api/admin/mosques`, cfg).then((r) => setMosques(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const approve = async (id) => {
    try { await axios.post(`${API_URL}/api/mosques/${id}/approve`, {}, cfg); toast.success('Masjid disetujui'); load(); }
    catch (e) { toast.error('Gagal menyetujui'); }
  };
  const del = async (id) => {
    if (!window.confirm('Hapus masjid ini beserta datanya?')) return;
    try { await axios.delete(`${API_URL}/api/mosques/${id}`, cfg); toast.success('Masjid dihapus'); load(); }
    catch (e) { toast.error('Gagal menghapus'); }
  };
  const FEATURES = [{ key: 'umkm', label: 'UMKM' }, { key: 'zakat', label: 'Zakat' }, { key: 'infaq_premium', label: 'Infaq Premium' }];
  const toggleFeature = async (m, key) => {
    const cur = { umkm: true, zakat: true, infaq_premium: false, ...(m.features || {}) }[key];
    try { await axios.put(`${API_URL}/api/admin/mosques/${m.id}/features`, { [key]: !cur }, cfg); load(); }
    catch (e) { toast.error('Gagal mengubah lisensi fitur'); }
  };
  const cycleSaas = async (m) => {
    const order = ['trial', 'active', 'suspended'];
    const next = order[(order.indexOf(m.saas_status || 'trial') + 1) % order.length];
    try { await axios.put(`${API_URL}/api/admin/mosques/${m.id}/saas-status`, { saas_status: next }, cfg); load(); }
    catch (e) { toast.error('Gagal mengubah status iuran'); }
  };
  const create = async (e) => {
    e.preventDefault();
    if (!form.village_id) return toast.error('Pilih wilayah lengkap sampai Desa/Kelurahan');
    try {
      await axios.post(`${API_URL}/api/mosques`, {
        name: form.name, village_id: form.village_id, address: form.address,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
      }, cfg);
      toast.success('Masjid dibuat & aktif');
      setForm({ name: '', village_id: '', address: '', latitude: '', longitude: '' });
      setShowForm(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Gagal membuat masjid'); }
  };

  const list = mosques.filter((m) => filter === 'all' || m.status === filter);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          {['all', 'pending', 'active'].map((f) => (
            <button key={f} data-testid={`masjid-filter-${f}`} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-sm font-body font-medium ${filter === f ? 'bg-emerald-700 text-white' : 'bg-white border border-stone-200 text-stone-600'}`}>
              {f === 'all' ? 'Semua' : f === 'pending' ? 'Menunggu' : 'Aktif'}
            </button>
          ))}
        </div>
        <button data-testid="add-mosque-btn" onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full text-sm font-medium">
          <Plus className="w-4 h-4" /> Tambah Masjid
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="bg-white rounded-2xl border border-stone-200 p-5 mb-6 space-y-3" data-testid="mosque-create-form">
          <input data-testid="mosque-name-input" className={inputCls} placeholder="Nama Masjid" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <RegionCascade onVillageChange={(vid) => setForm((f) => ({ ...f, village_id: vid }))} />
          <input className={inputCls} placeholder="Alamat" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="mosque-lat-input" className={inputCls} placeholder="Latitude (mis. -7.0466)" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} />
            <input data-testid="mosque-lng-input" className={inputCls} placeholder="Longitude (mis. 107.5195)" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} />
            <button
              type="button"
              onClick={() => {
                if (!navigator.geolocation) return toast.error('Browser tidak mendukung GPS');
                navigator.geolocation.getCurrentPosition(
                  (pos) => {
                    setForm(f => ({
                      ...f,
                      latitude: parseFloat(pos.coords.latitude.toFixed(7)).toString(),
                      longitude: parseFloat(pos.coords.longitude.toFixed(7)).toString(),
                    }));
                    toast.success('Koordinat GPS berhasil diambil');
                  },
                  () => toast.error('Gagal mendapatkan lokasi GPS')
                );
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium col-span-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
              Ambil Lokasi Saya (GPS)
            </button>
          </div>
          <button type="submit" data-testid="mosque-create-submit" className="w-full py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-medium">Simpan Masjid</button>
        </form>
      )}

      <div className="space-y-3">
        {list.map((m) => {
          const feats = { umkm: true, zakat: true, infaq_premium: false, ...(m.features || {}) };
          const saas = m.saas_status || 'trial';
          const saasCls = saas === 'active' ? 'bg-emerald-50 text-emerald-700' : saas === 'suspended' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700';
          const saasLabel = saas === 'active' ? 'Iuran Aktif' : saas === 'suspended' ? 'Ditangguhkan' : 'Trial';
          return (
          <div key={m.id} data-testid={`admin-mosque-${m.id}`} className="bg-white rounded-2xl border border-stone-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0"><Building2 className="w-6 h-6 text-emerald-600" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-body font-semibold text-text-primary truncate">{m.name}</h3>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${m.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                    {m.status === 'active' ? 'Aktif' : 'Menunggu'}
                  </span>
                </div>
                <p className="text-xs text-text-secondary truncate">{m.village_name}, {m.regency_name}, {m.province_name}</p>
              </div>
              {m.status === 'pending' && (
                <button data-testid={`approve-mosque-${m.id}`} onClick={() => approve(m.id)} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full text-xs font-medium">
                  <CheckCircle2 className="w-4 h-4" /> Setujui
                </button>
              )}
              <button data-testid={`delete-mosque-${m.id}`} onClick={() => del(m.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-600" /></button>
            </div>
            <div className="mt-3 pt-3 border-t border-stone-100 flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-body text-stone-400 mr-1">Lisensi:</span>
              {FEATURES.map((f) => (
                <button key={f.key} data-testid={`feature-${f.key}-${m.id}`} onClick={() => toggleFeature(m, f.key)}
                  className={`text-[11px] px-2.5 py-1 rounded-full font-medium border transition-colors ${feats[f.key] ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-stone-400 border-stone-200 hover:border-emerald-300'}`}>
                  {f.label}
                </button>
              ))}
              <button data-testid={`saas-status-${m.id}`} onClick={() => cycleSaas(m)} className={`ml-auto text-[11px] px-2.5 py-1 rounded-full font-medium flex items-center gap-1 ${saasCls}`}>
                <CreditCard className="w-3.5 h-3.5" /> {saasLabel}
              </button>
            </div>
          </div>
          );
        })}
        {list.length === 0 && <p className="text-center text-text-secondary font-body py-10">Tidak ada masjid.</p>}
      </div>
    </div>
  );
};

/* ---------------- Wilayah Tab ---------------- */
const Column = ({ title, items, selected, onSelect, onAdd, onDelete, placeholder, disabled, testid }) => {
  const [name, setName] = useState('');
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-3 flex flex-col" data-testid={testid}>
      <h4 className="text-xs font-body font-semibold text-stone-500 uppercase mb-2 px-1">{title}</h4>
      <div className="flex-1 space-y-1 mb-2 max-h-64 overflow-y-auto">
        {items.map((x) => (
          <div key={x.id} className={`group flex items-center justify-between px-2.5 py-2 rounded-lg cursor-pointer text-sm font-body ${selected === x.id ? 'bg-emerald-700 text-white' : 'hover:bg-stone-100 text-text-primary'}`} onClick={() => onSelect(x.id)}>
            <span className="truncate flex items-center gap-1">{x.name}{onSelect && <ChevronRight className="w-3.5 h-3.5 opacity-50" />}</span>
            <button onClick={(e) => { e.stopPropagation(); onDelete(x.id); }} className="opacity-0 group-hover:opacity-100"><Trash2 className={`w-3.5 h-3.5 ${selected === x.id ? 'text-white' : 'text-red-500'}`} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-stone-400 px-1 py-2">{disabled ? '—' : 'Belum ada data'}</p>}
      </div>
      {!disabled && (
        <div className="flex gap-1">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={placeholder} className="flex-1 px-2 py-1.5 rounded-lg border border-stone-200 text-sm outline-none focus:border-emerald-500" />
          <button data-testid={`${testid}-add-btn`} onClick={() => { if (name.trim()) { onAdd(name.trim()); setName(''); } }} className="px-2.5 bg-emerald-700 text-white rounded-lg"><Plus className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
};

const WilayahTab = () => {
  const [prov, setProv] = useState([]); const [reg, setReg] = useState([]); const [dist, setDist] = useState([]); const [vil, setVil] = useState([]);
  const [sp, setSp] = useState(''); const [sr, setSr] = useState(''); const [sd, setSd] = useState('');

  const fetchLevel = (level, parent, set) => axios.get(`${API_URL}/api/regions`, { params: parent ? { level, parent_id: parent } : { level } }).then((r) => set(r.data)).catch(() => {});
  useEffect(() => { fetchLevel('province', null, setProv); }, []);
  useEffect(() => { setSr(''); setSd(''); setReg([]); setDist([]); setVil([]); if (sp) fetchLevel('regency', sp, setReg); }, [sp]);
  useEffect(() => { setSd(''); setDist([]); setVil([]); if (sr) fetchLevel('district', sr, setDist); }, [sr]);
  useEffect(() => { setVil([]); if (sd) fetchLevel('village', sd, setVil); }, [sd]);

  const add = async (level, parent_id, name, reload) => {
    try { await axios.post(`${API_URL}/api/regions`, { name, level, parent_id }, cfg); toast.success('Wilayah ditambahkan'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Gagal menambah'); }
  };
  const del = async (id, reload) => {
    try { await axios.delete(`${API_URL}/api/regions/${id}`, cfg); toast.success('Wilayah dihapus'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Gagal menghapus (mungkin punya sub-wilayah)'); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
      <Column title="Provinsi" testid="col-province" items={prov} selected={sp} onSelect={setSp} placeholder="Provinsi baru"
        onAdd={(n) => add('province', null, n, () => fetchLevel('province', null, setProv))}
        onDelete={(id) => del(id, () => fetchLevel('province', null, setProv))} />
      <Column title="Kabupaten/Kota" testid="col-regency" items={reg} selected={sr} onSelect={setSr} placeholder="Kab/Kota baru" disabled={!sp}
        onAdd={(n) => add('regency', sp, n, () => fetchLevel('regency', sp, setReg))}
        onDelete={(id) => del(id, () => fetchLevel('regency', sp, setReg))} />
      <Column title="Kecamatan" testid="col-district" items={dist} selected={sd} onSelect={setSd} placeholder="Kecamatan baru" disabled={!sr}
        onAdd={(n) => add('district', sr, n, () => fetchLevel('district', sr, setDist))}
        onDelete={(id) => del(id, () => fetchLevel('district', sr, setDist))} />
      <Column title="Desa/Kelurahan" testid="col-village" items={vil} selected={null} onSelect={() => {}} placeholder="Desa/Kel baru" disabled={!sd}
        onAdd={(n) => add('village', sd, n, () => fetchLevel('village', sd, setVil))}
        onDelete={(id) => del(id, () => fetchLevel('village', sd, setVil))} />
    </div>
  );
};

/* ---------------- Takmir Tab ---------------- */
const TakmirTab = () => {
  const [takmirs, setTakmirs] = useState([]);
  const [mosques, setMosques] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', password: '', mosque_id: '' });

  const load = () => {
    axios.get(`${API_URL}/api/admin/takmir`, cfg).then((r) => setTakmirs(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/admin/mosques`, cfg).then((r) => setMosques(r.data)).catch(() => {});
  };
  useEffect(load, []);
  const mosqueName = (id) => mosques.find((m) => m.id === id)?.name || '—';

  const create = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/takmir`, form, cfg);
      toast.success('Takmir dibuat');
      setForm({ name: '', email: '', password: '', mosque_id: '' }); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Gagal membuat takmir'); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <form onSubmit={create} className="bg-white rounded-2xl border border-stone-200 p-5 space-y-3 h-fit" data-testid="takmir-create-form">
        <h3 className="font-heading font-semibold text-primary-dark">Tugaskan Takmir Baru</h3>
        <input data-testid="takmir-name-input" className={inputCls} placeholder="Nama Takmir" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <input data-testid="takmir-email-input" type="email" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
        <input data-testid="takmir-password-input" type="password" className={inputCls} placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
        <select data-testid="takmir-mosque-select" className={inputCls} value={form.mosque_id} onChange={(e) => setForm({ ...form, mosque_id: e.target.value })} required>
          <option value="">Pilih Masjid</option>
          {mosques.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.status})</option>)}
        </select>
        <button type="submit" data-testid="takmir-create-submit" className="w-full py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-medium">Buat Takmir</button>
      </form>
      <div className="space-y-2">
        <h3 className="font-heading font-semibold text-primary-dark mb-1">Daftar Takmir</h3>
        {takmirs.map((t, i) => (
          <div key={i} className="bg-white rounded-xl border border-stone-200 p-3">
            <p className="font-body font-medium text-text-primary text-sm">{t.name}</p>
            <p className="text-xs text-text-secondary">{t.email} · {mosqueName(t.mosque_id)}</p>
          </div>
        ))}
        {takmirs.length === 0 && <p className="text-text-secondary font-body text-sm py-6">Belum ada takmir.</p>}
      </div>
    </div>
  );
};

/* ---------------- Branding / CMS Tab ---------------- */
const CMSTab = () => {
  const [ps, setPs] = useState(null);
  useEffect(() => { axios.get(`${API_URL}/api/platform-settings`).then((r) => setPs(r.data)).catch(() => {}); }, []);
  const save = async () => {
    try { await axios.put(`${API_URL}/api/platform-settings`, ps, cfg); toast.success('Branding disimpan'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Gagal menyimpan'); }
  };
  if (!ps) return <p className="text-stone-500 font-body py-8">Memuat…</p>;
  const field = (label, key, ph) => (
    <div>
      <label className="block text-xs font-body font-semibold text-stone-500 uppercase mb-1">{label}</label>
      <input data-testid={`cms-${key}`} className={inputCls} value={ps[key] || ''} placeholder={ph || ''} onChange={(e) => setPs({ ...ps, [key]: e.target.value })} />
    </div>
  );
  return (
    <div className="max-w-2xl space-y-5" data-testid="cms-tab">
      <div className="bg-white rounded-2xl border border-stone-200 p-5 space-y-3">
        <h3 className="font-heading font-semibold text-primary-dark">Halaman Depan (Landing)</h3>
        {field('Judul', 'landing_title')}
        {field('Sub-judul', 'landing_subtitle')}
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 p-5 space-y-3">
        <h3 className="font-heading font-semibold text-primary-dark">Splashscreen</h3>
        {field('Judul Splashscreen', 'splash_title')}
        {field('Tagline Splashscreen', 'splash_tagline')}
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 p-5 space-y-3">
        <h3 className="font-heading font-semibold text-primary-dark">Footer Platform</h3>
        <label className="flex items-center justify-between gap-3 py-1">
          <span className="text-sm font-body text-text-primary">Tampilkan footer di setiap masjid</span>
          <input type="checkbox" data-testid="cms-footer_enabled" checked={!!ps.footer_enabled} onChange={(e) => setPs({ ...ps, footer_enabled: e.target.checked })} className="w-5 h-5 accent-emerald-700" />
        </label>
        {field('Teks Footer', 'footer_text')}
        {field('URL Footer', 'footer_url')}
      </div>
      <button data-testid="cms-save-btn" onClick={save} className="flex items-center gap-2 px-5 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-medium">
        <Save className="w-4 h-4" /> Simpan Perubahan
      </button>
    </div>
  );
};

/* ---------------- Analitik Regional Tab ---------------- */
const Stat = ({ label, value }) => (
  <div className="bg-white rounded-2xl border border-stone-200 p-4">
    <p className="text-xs font-body text-stone-500">{label}</p>
    <p className="text-2xl font-heading font-bold text-primary-dark mt-1">{value}</p>
  </div>
);

const AnalitikTab = () => {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API_URL}/api/admin/analytics/regional`, cfg).then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <p className="text-stone-500 font-body py-8">Memuat…</p>;
  const rp = (n) => 'Rp ' + Math.round(n || 0).toLocaleString('id-ID');
  return (
    <div data-testid="analytics-tab">
      <p className="text-sm font-body text-stone-500 mb-4">Data Infaq &amp; keuangan per Kabupaten/Kota di <span className="font-semibold text-primary-dark">{data.province}</span></p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <Stat label="Total Masjid" value={data.totals.mosque_count || 0} />
        <Stat label="Total Infaq Terkumpul" value={rp(data.totals.total_infaq)} />
        <Stat label="Total Pemasukan" value={rp(data.totals.total_income)} />
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden overflow-x-auto">
        <table className="w-full text-sm font-body">
          <thead className="bg-stone-50 text-stone-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Kabupaten/Kota</th>
              <th className="text-right px-4 py-3">Masjid</th>
              <th className="text-right px-4 py-3">Infaq</th>
              <th className="text-right px-4 py-3">Pemasukan</th>
            </tr>
          </thead>
          <tbody>
            {data.regencies.map((r) => (
              <tr key={r.regency_id} data-testid={`analytics-row-${r.regency_id}`} className="border-t border-stone-100">
                <td className="px-4 py-3 text-text-primary">{r.regency_name}</td>
                <td className="px-4 py-3 text-right">{r.mosque_count}</td>
                <td className="px-4 py-3 text-right text-emerald-700 font-medium">{rp(r.total_infaq)}</td>
                <td className="px-4 py-3 text-right">{rp(r.total_income)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ---------------- Sistem: Bagi Hasil & Iuran Tab ---------------- */
const SistemTab = () => {
  const [ps, setPs] = useState(null);
  useEffect(() => { axios.get(`${API_URL}/api/platform-settings`).then((r) => setPs(r.data)).catch(() => {}); }, []);
  const save = async () => {
    try { await axios.put(`${API_URL}/api/platform-settings`, ps, cfg); toast.success('Pengaturan sistem disimpan'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Gagal menyimpan'); }
  };
  if (!ps) return <p className="text-stone-500 font-body py-8">Memuat…</p>;

  const num = (label, key, suffix) => (
    <div>
      <label className="block text-[11px] font-body font-semibold text-stone-500 uppercase mb-1">{label}</label>
      <div className="relative">
        <input type="number" data-testid={`ps-${key}`} className={inputCls}
          value={ps[key] ?? ''} onChange={(e) => setPs({ ...ps, [key]: e.target.value === '' ? '' : parseFloat(e.target.value) })} />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-stone-400">{suffix}</span>}
      </div>
    </div>
  );
  const txt = (label, key, ph) => (
    <div>
      <label className="block text-[11px] font-body font-semibold text-stone-500 uppercase mb-1">{label}</label>
      <input data-testid={`saas-${key}`} className={inputCls} value={ps[key] || ''} placeholder={ph || ''} onChange={(e) => setPs({ ...ps, [key]: e.target.value })} />
    </div>
  );
  const sum = (...keys) => keys.reduce((a, k) => a + (parseFloat(ps[k]) || 0), 0);

  const Card = ({ title, children, total }) => (
    <div className="bg-white rounded-2xl border border-stone-200 p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold text-primary-dark">{title}</h3>
        {total !== undefined && (
          <span className={`text-xs font-body font-semibold px-2 py-0.5 rounded-full ${total === 100 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
            Total {total}%
          </span>
        )}
      </div>
      {children}
    </div>
  );

  return (
    <div className="max-w-3xl space-y-5" data-testid="sistem-tab">
      <p className="text-sm font-body text-stone-500">Atur persentase bagi hasil otomatis & iuran sistem. Persentase harus berjumlah 100%.</p>

      <Card title="Infaq Biasa" total={sum('ps_infaq_mosque', 'ps_infaq_pengurus', 'ps_infaq_system')}>
        <div className="grid grid-cols-3 gap-3">
          {num('Masjid', 'ps_infaq_mosque', '%')}
          {num('Pengurus', 'ps_infaq_pengurus', '%')}
          {num('Sistem', 'ps_infaq_system', '%')}
        </div>
      </Card>

      <Card title="Infaq Hadiah Umroh" total={sum('ps_umroh_tabungan', 'ps_umroh_mosque', 'ps_umroh_pengurus', 'ps_umroh_system')}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {num('Tabungan Umroh', 'ps_umroh_tabungan', '%')}
          {num('Masjid', 'ps_umroh_mosque', '%')}
          {num('Pengurus', 'ps_umroh_pengurus', '%')}
          {num('Sistem', 'ps_umroh_system', '%')}
        </div>
      </Card>

      <Card title="UMKM" total={sum('ps_umkm_mosque', 'ps_umkm_system')}>
        <div className="grid grid-cols-2 gap-3">
          {num('Masjid', 'ps_umkm_mosque', '%')}
          {num('Sistem', 'ps_umkm_system', '%')}
        </div>
      </Card>

      <Card title="Zakat">
        <div className="grid grid-cols-2 gap-3">
          {num('Biaya Layanan per Jiwa', 'ps_zakat_fee_per_jiwa', 'Rp')}
        </div>
      </Card>

      <Card title="Iuran Sistem (SaaS) & Rekening Tujuan">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {num('Iuran Bulanan', 'saas_monthly_fee', 'Rp')}
          {txt('Nomor WA Admin', 'saas_wa_number', '6281268035961')}
          {txt('Nama Bank', 'saas_bank_name', 'BNI')}
          {txt('Nomor Rekening', 'saas_bank_account', '7380333368')}
          {txt('Atas Nama', 'saas_bank_holder', 'Zulfikar')}
        </div>
      </Card>

      <button data-testid="sistem-save-btn" onClick={save} className="flex items-center gap-2 px-5 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-medium">
        <Save className="w-4 h-4" /> Simpan Pengaturan Sistem
      </button>
    </div>
  );
};

/* ---------------- Kemenag Tab ---------------- */
const KemenagTab = () => {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'kemenag_kab', region_id: '', region_name: '', phone: '' });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regions, setRegions] = useState([]);

  const load = () => axios.get(`${API_URL}/api/auth/kemenag-accounts`, cfg).then(r => setAccounts(r.data)).catch(() => {});
  const loadRegions = () => axios.get(`${API_URL}/api/regions`, { ...cfg, params: { level: 'regency' } }).then(r => setRegions(r.data)).catch(() => {});

  useEffect(() => { load(); loadRegions(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.name) return toast.error('Lengkapi semua field wajib');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/auth/create-kemenag`, form, cfg);
      toast.success('Akun Kemenag berhasil dibuat');
      setShowForm(false);
      setForm({ email: '', password: '', name: '', role: 'kemenag_kab', region_id: '', region_name: '', phone: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Gagal membuat akun'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Hapus akun ${email}?`)) return;
    try { await axios.delete(`${API_URL}/api/auth/kemenag-accounts/${encodeURIComponent(email)}`, cfg); toast.success('Akun dihapus'); load(); }
    catch { toast.error('Gagal menghapus'); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-stone-700">Akun Kemenag</h3>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full text-sm font-medium">
          <Plus className="w-4 h-4" /> Buat Akun Kemenag
        </button>
      </div>
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border border-stone-200 rounded-2xl p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-stone-500 mb-1.5">Email *</label>
              <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className={inputCls} type="email" placeholder="kemenag@kepri.go.id" required />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1.5">Password *</label>
              <input value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                className={inputCls} type="password" placeholder="Min. 8 karakter" required />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1.5">Nama Lengkap *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className={inputCls} placeholder="Nama admin kemenag" required />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1.5">Role</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} className={inputCls}>
                <option value="kemenag_kab">Kemenag Kabupaten/Kota</option>
                <option value="kemenag_prov">Kemenag Provinsi</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1.5">Wilayah</label>
              <select value={form.region_id}
                onChange={e => {
                  const sel = regions.find(r => r.id === e.target.value);
                  setForm(f => ({ ...f, region_id: e.target.value, region_name: sel?.name || '' }));
                }}
                className={inputCls}>
                <option value="">— Pilih Wilayah —</option>
                {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1.5">No. HP</label>
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                className={inputCls} placeholder="08xxxxxxxxxx" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-stone-200 rounded-xl text-sm text-stone-600 hover:bg-stone-50">Batal</button>
            <button type="submit" disabled={saving} className="px-5 py-2 bg-emerald-700 text-white rounded-xl text-sm font-medium disabled:opacity-60">
              {saving ? 'Membuat...' : 'Buat Akun'}
            </button>
          </div>
        </form>
      )}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        {accounts.length === 0 ? (
          <div className="p-10 text-center text-stone-400 text-sm">Belum ada akun Kemenag</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-xs text-stone-500">
              <tr>
                <th className="px-4 py-3 text-left">Nama</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Wilayah</th>
                <th className="px-4 py-3 text-center">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {accounts.map(acc => (
                <tr key={acc.email} className="hover:bg-stone-50">
                  <td className="px-4 py-3 font-medium text-stone-800">{acc.name}</td>
                  <td className="px-4 py-3 text-stone-600">{acc.email}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${acc.role === 'kemenag_prov' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                      {acc.role === 'kemenag_prov' ? 'Kemenag Prov' : 'Kemenag Kab/Kota'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-stone-500">{acc.region_name || '-'}</td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => handleDelete(acc.email)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/* ---------------- Zakat Settings Tab ---------------- */
const ZakatSettingsTab = () => {
  const [settings, setSettings] = useState([]);
  const [regions, setRegions] = useState([]);
  const [form, setForm] = useState({ region_id: 'global', region_name: 'Global (Default)', nilai_beras_per_jiwa: 2.5, harga_beras_per_kg: 15000, nominal_uang_per_jiwa: 37500, tahun: new Date().getFullYear() });
  const [saving, setSaving] = useState(false);

  const load = () => {
    axios.get(`${API_URL}/api/zakat-ramadan/wilayah-settings`, cfg).then(r => setSettings(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/regions`, { ...cfg, params: { level: 'regency' } }).then(r => setRegions(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...form, [name]: value };
    if (name === 'nilai_beras_per_jiwa' || name === 'harga_beras_per_kg') {
      updated.nominal_uang_per_jiwa = parseFloat(updated.nilai_beras_per_jiwa || 0) * parseFloat(updated.harga_beras_per_kg || 0);
    }
    if (name === 'region_id') {
      const sel = regions.find(r => r.id === value);
      updated.region_name = sel?.name || (value === 'global' ? 'Global (Default)' : value);
    }
    setForm(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/zakat-ramadan/wilayah-settings`, form, cfg);
      toast.success('Pengaturan wilayah disimpan');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Gagal menyimpan'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <h3 className="font-semibold text-stone-700">Pengaturan Nilai Zakat per Wilayah</h3>
      <form onSubmit={handleSubmit} className="bg-white border border-stone-200 rounded-2xl p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-stone-500 mb-1.5">Wilayah</label>
            <select name="region_id" value={form.region_id} onChange={handleChange} className={inputCls}>
              <option value="global">Global (Default untuk semua)</option>
              {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-stone-500 mb-1.5">Tahun</label>
            <input name="tahun" type="number" value={form.tahun} onChange={handleChange} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-stone-500 mb-1.5">Beras per Jiwa (kg)</label>
            <input name="nilai_beras_per_jiwa" type="number" step="0.1" value={form.nilai_beras_per_jiwa} onChange={handleChange} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-stone-500 mb-1.5">Harga Beras per kg (Rp)</label>
            <input name="harga_beras_per_kg" type="number" value={form.harga_beras_per_kg} onChange={handleChange} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-stone-500 mb-1.5">Setara Uang per Jiwa (Rp)</label>
            <input name="nominal_uang_per_jiwa" type="number" value={form.nominal_uang_per_jiwa} onChange={handleChange} className={inputCls} />
            <p className="text-xs text-stone-400 mt-1">{form.nilai_beras_per_jiwa} kg × Rp {Number(form.harga_beras_per_kg).toLocaleString('id-ID')} = Rp {Number(form.nominal_uang_per_jiwa).toLocaleString('id-ID')}</p>
          </div>
        </div>
        <button type="submit" disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-emerald-700 text-white rounded-xl text-sm font-medium disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? 'Menyimpan...' : 'Simpan Pengaturan'}
        </button>
      </form>
      {settings.length > 0 && (
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <div className="px-5 py-3 border-b text-sm font-semibold text-stone-700">Pengaturan Tersimpan</div>
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-xs text-stone-500">
              <tr>
                <th className="px-4 py-3 text-left">Wilayah</th>
                <th className="px-4 py-3 text-center">Tahun</th>
                <th className="px-4 py-3 text-right">Beras/Jiwa</th>
                <th className="px-4 py-3 text-right">Harga/kg</th>
                <th className="px-4 py-3 text-right">Uang/Jiwa</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {settings.map(s => (
                <tr key={s.region_id} className="hover:bg-stone-50">
                  <td className="px-4 py-3 font-medium text-stone-800">{s.region_name}</td>
                  <td className="px-4 py-3 text-center text-stone-600">{s.tahun}</td>
                  <td className="px-4 py-3 text-right text-amber-700 font-medium">{s.nilai_beras_per_jiwa} kg</td>
                  <td className="px-4 py-3 text-right text-stone-600">Rp {Number(s.harga_beras_per_kg).toLocaleString('id-ID')}</td>
                  <td className="px-4 py-3 text-right text-emerald-700 font-semibold">Rp {Number(s.nominal_uang_per_jiwa).toLocaleString('id-ID')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ---------------- Panel ---------------- */
const SuperAdminPanel = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('mosques');

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const tabs = [
    { id: 'mosques', label: 'Masjid', icon: Building2 },
    { id: 'regions', label: 'Wilayah', icon: MapPin },
    { id: 'takmir', label: 'Takmir', icon: Users },
    { id: 'kemenag', label: 'Kemenag', icon: UserCheck },
    { id: 'upz-registrations', label: 'Daftar UPZ', icon: ClipboardList },
    { id: 'zakat-settings', label: 'Zakat Ramadan', icon: Wheat },
    { id: 'cms', label: 'Branding', icon: Palette },
    { id: 'sistem', label: 'Bagi Hasil', icon: Percent },
    { id: 'analytics', label: 'Analitik', icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="superadmin-panel">
      <Toaster position="top-right" richColors />
      <div className="bg-[#022C22] text-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-400/90 flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-emerald-950" /></div>
            <div>
              <h1 className="text-xl font-heading font-bold">Super Admin</h1>
              <p className="text-xs text-white/70">{user?.email}</p>
            </div>
          </div>
          <button data-testid="superadmin-logout-btn" onClick={handleLogout} className="p-2 rounded-full hover:bg-white/10"><LogOut className="w-5 h-5" /></button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl border border-stone-200 p-2 mb-8 flex gap-2 overflow-x-auto">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} data-testid={`superadmin-tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-body font-medium text-sm ${tab === t.id ? 'bg-emerald-700 text-white' : 'text-stone-600 hover:bg-stone-50'}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
        <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          {tab === 'mosques' && <MasjidTab />}
          {tab === 'regions' && <WilayahTab />}
          {tab === 'takmir' && <TakmirTab />}
          {tab === 'kemenag' && <KemenagTab />}
          {tab === 'zakat-settings' && <ZakatSettingsTab />}
          {tab === 'cms' && <CMSTab />}
          {tab === 'sistem' && <SistemTab />}
          {tab === 'analytics' && <AnalitikTab />}
        </motion.div>
      </div>
    </div>
  );
};

export default SuperAdminPanel;

